/**
 * Vendor portal — Order detail.
 * Shows line items, buyer + shipping info, and lets the vendor mark
 * fulfilled/shipped (with tracking) or add internal notes.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useVendor } from "@/lib/store/useVendor";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { formatPrice } from "@/store/storeCart";
import { ArrowLeft, Package, Truck, CheckCircle2, ExternalLink } from "lucide-react";

type Order = {
  id: string;
  order_number: string;
  status: string;
  subtotal_cents: number;
  tax_cents: number;
  shipping_cents: number;
  total_cents: number;
  vendor_payout_cents: number;
  platform_fee_cents: number;
  refunded_cents: number;
  shipping_address: Record<string, unknown> | null;
  shipping_method: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  fulfilled_at: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  buyer_notes: string | null;
  vendor_notes: string | null;
  created_at: string;
  buyer_id: string;
};

type Item = {
  id: string;
  product_title: string;
  variant_title: string | null;
  product_type: string;
  quantity: number;
  unit_price_cents: number;
  subtotal_cents: number;
};

const VendorOrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { vendor } = useVendor();
  const { toast } = useToast();
  const [order, setOrder] = useState<Order | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [buyerName, setBuyerName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tracking, setTracking] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [notes, setNotes] = useState("");

  const load = async () => {
    if (!id || !vendor) return;
    const { data: o } = await supabase
      .from("store_orders")
      .select(
        "id, order_number, status, subtotal_cents, tax_cents, shipping_cents, total_cents, vendor_payout_cents, platform_fee_cents, refunded_cents, shipping_address, shipping_method, tracking_number, tracking_url, fulfilled_at, shipped_at, delivered_at, buyer_notes, vendor_notes, created_at, buyer_id",
      )
      .eq("id", id)
      .eq("vendor_id", vendor.id)
      .maybeSingle();
    if (!o) {
      setLoading(false);
      return;
    }
    setOrder(o as Order);
    setTracking((o as Order).tracking_number ?? "");
    setTrackingUrl((o as Order).tracking_url ?? "");
    setNotes((o as Order).vendor_notes ?? "");

    const [itemsRes, buyerRes] = await Promise.all([
      supabase
        .from("store_order_items")
        .select(
          "id, product_title, variant_title, product_type, quantity, unit_price_cents, subtotal_cents",
        )
        .eq("order_id", id),
      supabase.from("profiles").select("display_name").eq("id", (o as Order).buyer_id).maybeSingle(),
    ]);
    setItems((itemsRes.data as Item[]) ?? []);
    setBuyerName((buyerRes.data as { display_name: string } | null)?.display_name ?? "Buyer");
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, vendor]);

  const updateOrder = async (
    updates: Record<string, unknown>,
    successMsg: string,
  ) => {
    if (!order) return;
    setSaving(true);
    const { error } = await supabase
      .from("store_orders")
      .update(updates as never)
      .eq("id", order.id);
    setSaving(false);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: successMsg });
    void load();
  };

  if (loading) return <Skeleton className="h-96" />;
  if (!order) {
    return (
      <Card className="p-12 text-center">
        <p className="text-muted-foreground">Order not found.</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate("/store/vendor/orders")}>
          Back to orders
        </Button>
      </Card>
    );
  }

  const addr = order.shipping_address ?? {};
  const isPhysical = items.some((i) => i.product_type === "physical");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/store/vendor/orders")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <Badge variant="secondary" className="capitalize">
          {order.status.replace(/_/g, " ")}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">{order.order_number}</h2>
              <p className="text-xs text-muted-foreground">
                {new Date(order.created_at).toLocaleString()}
              </p>
            </div>
            <Separator />
            {items.map((item) => (
              <div key={item.id} className="flex items-start justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="font-medium">{item.product_title}</p>
                  {item.variant_title && (
                    <p className="text-xs text-muted-foreground">{item.variant_title}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {item.quantity} × {formatPrice(item.unit_price_cents)} · {item.product_type}
                  </p>
                </div>
                <p className="font-medium whitespace-nowrap">
                  {formatPrice(item.subtotal_cents)}
                </p>
              </div>
            ))}
            <Separator />
            <div className="space-y-1 text-sm">
              <Row label="Subtotal" value={formatPrice(order.subtotal_cents)} />
              {order.tax_cents > 0 && <Row label="Tax" value={formatPrice(order.tax_cents)} />}
              {order.shipping_cents > 0 && (
                <Row label="Shipping" value={formatPrice(order.shipping_cents)} />
              )}
              <Row label="Total" value={formatPrice(order.total_cents)} bold />
              {order.refunded_cents > 0 && (
                <Row
                  label="Refunded"
                  value={`− ${formatPrice(order.refunded_cents)}`}
                  className="text-destructive"
                />
              )}
              <Separator className="my-2" />
              <Row
                label="Platform fee"
                value={`− ${formatPrice(order.platform_fee_cents)}`}
                className="text-muted-foreground"
              />
              <Row
                label="Your payout"
                value={formatPrice(order.vendor_payout_cents)}
                bold
                className="text-primary"
              />
            </div>
          </Card>

          {isPhysical && (
            <Card className="p-5 space-y-3">
              <h3 className="font-semibold">Fulfillment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Tracking number</Label>
                  <Input
                    value={tracking}
                    onChange={(e) => setTracking(e.target.value)}
                    placeholder="1Z…"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tracking URL (optional)</Label>
                  <Input
                    value={trackingUrl}
                    onChange={(e) => setTrackingUrl(e.target.value)}
                    placeholder="https://…"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {!order.fulfilled_at && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      updateOrder(
                        { status: "fulfilled", fulfilled_at: new Date().toISOString() },
                        "Marked as fulfilled",
                      )
                    }
                    disabled={saving}
                  >
                    <Package className="h-4 w-4 mr-2" /> Mark fulfilled
                  </Button>
                )}
                {!order.shipped_at && (
                  <Button
                    size="sm"
                    onClick={() =>
                      updateOrder(
                        {
                          status: "shipped",
                          shipped_at: new Date().toISOString(),
                          fulfilled_at: order.fulfilled_at ?? new Date().toISOString(),
                          tracking_number: tracking || null,
                          tracking_url: trackingUrl || null,
                        },
                        "Marked as shipped",
                      )
                    }
                    disabled={saving}
                  >
                    <Truck className="h-4 w-4 mr-2" /> Mark shipped
                  </Button>
                )}
                {!order.delivered_at && order.shipped_at && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      updateOrder(
                        {
                          status: "delivered",
                          delivered_at: new Date().toISOString(),
                        },
                        "Marked as delivered",
                      )
                    }
                    disabled={saving}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Mark delivered
                  </Button>
                )}
              </div>
              {order.tracking_url && (
                <a
                  href={order.tracking_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center text-sm text-primary hover:underline"
                >
                  Open tracking <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              )}
            </Card>
          )}

          <Card className="p-5 space-y-3">
            <h3 className="font-semibold">Internal notes</h3>
            <Textarea
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes only you can see…"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => updateOrder({ vendor_notes: notes }, "Notes saved")}
              disabled={saving}
            >
              Save notes
            </Button>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-5 space-y-2">
            <h3 className="font-semibold text-sm">Customer</h3>
            <p className="text-sm">{buyerName}</p>
            {order.buyer_notes && (
              <>
                <Separator />
                <p className="text-xs text-muted-foreground">Buyer note:</p>
                <p className="text-sm italic">"{order.buyer_notes}"</p>
              </>
            )}
          </Card>

          {isPhysical && (
            <Card className="p-5 space-y-1 text-sm">
              <h3 className="font-semibold mb-2">Ship to</h3>
              {addr.name ? <p>{String(addr.name)}</p> : null}
              {addr.line1 ? <p>{String(addr.line1)}</p> : null}
              {addr.line2 ? <p>{String(addr.line2)}</p> : null}
              {(addr.city || addr.state || addr.postal_code) && (
                <p>
                  {[addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}
                </p>
              )}
              {addr.country ? <p>{String(addr.country)}</p> : null}
              {!addr.line1 && (
                <p className="text-muted-foreground italic">No shipping address on file.</p>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({
  label,
  value,
  bold,
  className,
}: {
  label: string;
  value: string;
  bold?: boolean;
  className?: string;
}) => (
  <div className={`flex justify-between ${className ?? ""}`}>
    <span className={bold ? "font-semibold" : ""}>{label}</span>
    <span className={bold ? "font-semibold" : ""}>{value}</span>
  </div>
);

export default VendorOrderDetail;
